import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertTriangle, CheckCircle2, Circle, RefreshCcw, HeartPulse,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import IntegrationHistorySparkline from './IntegrationHistorySparkline';
import UptimeSlaChip from './UptimeSlaChip';

const STATUS_VARIANT = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  error: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_ICON = {
  ok: <CheckCircle2 className="h-3.5 w-3.5" />,
  warning: <AlertTriangle className="h-3.5 w-3.5" />,
  error: <AlertTriangle className="h-3.5 w-3.5" />,
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  } catch (_) { return ''; }
};

const AdminIntegrationsPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [guardrails, setGuardrails] = useState(null);
  const [boots, setBoots] = useState(null);
  const [pii, setPii] = useState(null);
  const [audit, setAudit] = useState(null);
  const [emqCfg, setEmqCfg] = useState(null);
  const [emqRefreshing, setEmqRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      const url = force
        ? '/admin/integrations/health?refresh=true'
        : '/admin/integrations/health';
      const [{ data: healthData }, { data: histData },
             { data: guardData }, { data: bootData },
             { data: piiData },
             { data: auditData },
             { data: emqData }] = await Promise.all([
        api.get(url),
        api.get('/admin/integrations/history?hours=24&bucket_minutes=30'),
        api.get('/admin/integrations/config-guardrails').catch(() => ({ data: null })),
        api.get('/admin/boot-history?limit=5').catch(() => ({ data: null })),
        api.get('/admin/integrations/pii-linter-status').catch(() => ({ data: null })),
        api.get('/admin/integrations/security-audit').catch(() => ({ data: null })),
        api.get('/admin/integrations/meta-emq-config').catch(() => ({ data: null })),
      ]);
      setData(healthData);
      setHistory(histData);
      setGuardrails(guardData);
      setBoots(bootData);
      setPii(piiData);
      setAudit(auditData);
      setEmqCfg(emqData);
    } catch (_) {
      toast.error('Could not load integrations health');
    }
  };

  useEffect(() => { load(); }, []);

  const historyByKey = useMemo(() => {
    const map = {};
    (history?.integrations || []).forEach((it) => { map[it.key] = it; });
    return map;
  }, [history]);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: healthData } = await api.post('/admin/integrations/refresh');
      setData(healthData);
      // History caches 60s server-side; still ask for a fresh read so a
      // just-recorded snapshot lands on the strip immediately.
      try {
        const { data: histData } = await api.get(
          '/admin/integrations/history?hours=24&bucket_minutes=30',
        );
        setHistory(histData);
      } catch (_) { /* non-fatal */ }
      toast.success('Refreshed');
    } catch (_) {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) {
    return (
      <div className="p-8 text-sm text-neutral-500">Loading integrations health...</div>
    );
  }

  const overall = data.overall_status;

  return (
    <div className="p-8 space-y-5 max-w-6xl" data-testid="admin-integrations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <HeartPulse className="h-5 w-5 text-primary-800" />
            <h1 className="font-display text-3xl font-bold text-primary-900">Integrations health</h1>
          </div>
          <p className="text-sm text-neutral-600 mt-1">Third-party providers and background jobs. Checked every 5 minutes.</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_VARIANT[overall]}`}
            data-testid="integrations-overall-status"
          >
            {STATUS_ICON[overall]}
            {overall === 'ok' ? 'All systems operational'
              : overall === 'warning' ? `${data.counts.warning} warning${data.counts.warning === 1 ? '' : 's'}`
              : `${data.counts.error} error${data.counts.error === 1 ? '' : 's'}`}
          </span>
          {isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={() => forceRefresh()} disabled={refreshing} data-testid="integrations-refresh-btn">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh now'}
            </Button>
          )}
        </div>
      </div>

      {/* Bundle 30 - production hardening banners. Rendered ABOVE the
          integration cards so ops cannot miss them. Only red when a
          real production guardrail is failing (Stripe secret missing
          etc.); amber when dev-only mode is running with a wildcard. */}
      {(guardrails?.issues || []).map((iss) => (
        <div
          key={iss.code}
          className={`rounded-lg border p-3 text-sm ${
            iss.severity === 'critical'
              ? 'border-rose-300 bg-rose-50 text-rose-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}
          data-testid={`admin-integrations-guardrail-${iss.code}`}
        >
          <div className="font-semibold uppercase text-[10px] tracking-widest mb-0.5">
            {iss.severity === 'critical' ? 'Critical' : 'Warning'} - {iss.code.replace(/_/g, ' ')}
          </div>
          <div>{iss.message}</div>
        </div>
      ))}

      {/* Bundle 48.11 - Meta Marketing EMQ read-token config row. */}
      {emqCfg && (
        <Card
          className={`border ${emqCfg.token_configured ? 'border-emerald-200' : 'border-amber-200'}`}
          data-testid="admin-integrations-meta-emq-config"
        >
          <CardContent className="p-4 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">
                Meta Marketing
              </div>
              <div className="text-sm font-semibold text-primary-900">
                {emqCfg.label}
              </div>
              <div className="text-xs text-neutral-600 mt-1 max-w-md">
                {emqCfg.notes}
              </div>
              <div className="text-[11px] text-neutral-500 mt-2">
                Env var:{' '}
                <code className="rounded bg-neutral-100 px-1 py-0.5">
                  {emqCfg.env_var_name}
                </code>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 min-w-[160px]">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  emqCfg.token_configured
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}
                data-testid="admin-integrations-meta-emq-status"
              >
                {emqCfg.token_configured ? 'Configured' : 'Not configured'}
              </span>
              <a
                href={emqCfg.instructions_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-primary-800 hover:underline"
                data-testid="admin-integrations-meta-emq-instructions"
              >
                Where to find the token
              </a>
              {emqCfg.last_pull?.checked_at && (
                <div className="text-[10px] text-neutral-500">
                  Last pulled {new Date(emqCfg.last_pull.checked_at).toLocaleString()}
                </div>
              )}
              {isSuperAdmin && emqCfg.token_configured && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={emqRefreshing}
                  onClick={async () => {
                    setEmqRefreshing(true);
                    try {
                      const { data: pulled } = await api.post(
                        '/admin/integrations/meta-emq-config/refresh',
                      );
                      if (pulled?.skipped === 'no_token') {
                        toast.error('EMQ token not configured.');
                      } else {
                        toast.success('Meta reported EMQ refreshed.');
                        const { data: freshCfg } = await api.get(
                          '/admin/integrations/meta-emq-config',
                        );
                        setEmqCfg(freshCfg);
                      }
                    } catch (_) {
                      toast.error('Refresh failed.');
                    } finally {
                      setEmqRefreshing(false);
                    }
                  }}
                  data-testid="admin-integrations-meta-emq-refresh"
                >
                  <RefreshCcw
                    className={`h-3.5 w-3.5 mr-1 ${emqRefreshing ? 'animate-spin' : ''}`}
                  />
                  {emqRefreshing ? 'Refreshing...' : 'Refresh from Meta'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {boots?.boots?.length ? (
        <Card
          className={`border ${boots.rapid_restart_detected ? 'border-rose-300' : 'border-neutral-200'}`}
          data-testid="admin-integrations-recent-boots"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-primary-900">Recent boots</div>
              {boots.rapid_restart_detected && (
                <span
                  className="inline-flex items-center rounded-full border border-rose-300 bg-rose-50 text-rose-900 text-[10px] font-semibold px-2 py-0.5"
                  data-testid="admin-integrations-rapid-restart-flag"
                >
                  Rapid restart detected
                </span>
              )}
            </div>
            <table className="w-full text-xs" data-testid="admin-integrations-boots-table">
              <thead className="text-left text-[10px] uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="py-1">Boot time</th>
                  <th className="py-1">PID</th>
                  <th className="py-1">Prev uptime</th>
                  <th className="py-1">Exit reason</th>
                  <th className="py-1">Stripe secret</th>
                  <th className="py-1">CORS</th>
                </tr>
              </thead>
              <tbody>
                {boots.boots.map((b) => (
                  <tr key={b.id} className="border-t border-neutral-100">
                    <td className="py-1 tabular-nums">{fmtAgo(b.boot_at)}</td>
                    <td className="py-1 tabular-nums">{b.pid}</td>
                    <td className="py-1 tabular-nums">
                      {b.prev_uptime_seconds == null ? '-'
                        : b.prev_uptime_seconds < 60
                        ? <span className="text-rose-800 font-semibold">{b.prev_uptime_seconds}s</span>
                        : `${b.prev_uptime_seconds}s`}
                    </td>
                    <td className="py-1">
                      {b.exit_reason === 'sudden_death'
                        ? <span className="text-rose-800 font-semibold" title="No exit_reason recorded - SIGKILL / OOM / panic">sudden_death</span>
                        : b.exit_reason
                          ? <span className="text-neutral-700">{b.exit_reason}</span>
                          : <span className="text-neutral-400">running</span>}
                    </td>
                    <td className="py-1">
                      {b.stripe_webhook_secret_present
                        ? <span className="text-emerald-800">present</span>
                        : <span className="text-rose-800 font-semibold">missing</span>}
                    </td>
                    <td className="py-1">
                      {b.cors_wildcard
                        ? <span className="text-amber-800 font-semibold">*</span>
                        : <span className="text-emerald-800">explicit</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(() => {
              const oomCount = (boots.boots || [])
                .filter((b) => b.exit_reason === 'sudden_death').length;
              if (oomCount >= 2) {
                return (
                  <div className="mt-2 text-[11px] font-semibold text-rose-800" data-testid="admin-integrations-oom-suspected">
                    Possible OOM: {oomCount} sudden_death exits in last 5 boots - investigate memory limits.
                  </div>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>
      ) : null}

      {pii ? (
        <Card
          className={`border ${pii.status === 'critical' ? 'border-rose-300' : 'border-emerald-200'}`}
          data-testid="admin-integrations-pii-linter-panel"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-primary-900">PII linter status</div>
              <span
                className={`inline-flex items-center rounded-full border text-[10px] font-semibold px-2 py-0.5 ${
                  pii.status === 'critical'
                    ? 'border-rose-300 bg-rose-50 text-rose-900'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                }`}
                data-testid="admin-integrations-pii-status-pill"
              >
                {pii.status === 'critical' ? `${pii.match_count} matches` : 'OK'}
              </span>
            </div>
            <div className="text-[11px] text-neutral-600 grid gap-1 md:grid-cols-3">
              <div>Last scan: <span className="tabular-nums">{pii.last_scan_at ? fmtAgo(pii.last_scan_at) : 'never'}</span></div>
              <div>Denylist size: <span className="tabular-nums">{pii.denylist_size}</span></div>
              <div>Denylist refreshed: <span className="tabular-nums">{pii.denylist_last_refresh_at ? fmtAgo(pii.denylist_last_refresh_at) : 'never'}</span></div>
            </div>
            {pii.sample_matches?.length > 0 && (
              <pre className="mt-2 text-[10px] bg-rose-50 border border-rose-200 rounded p-2 max-h-40 overflow-auto" data-testid="admin-integrations-pii-matches">
                {pii.sample_matches.join('\n')}
              </pre>
            )}
          </CardContent>
        </Card>
      ) : null}

      {audit ? (
        <Card
          className={`border ${audit.score >= 90 ? 'border-emerald-200' : audit.score >= 70 ? 'border-amber-200' : 'border-rose-300'}`}
          data-testid="admin-integrations-security-audit-panel"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-primary-900">
                Security audit
                <span className="ml-2 text-neutral-500 font-normal">score {audit.score}/100</span>
                {audit.prev_score != null && audit.prev_score !== audit.score && (
                  <span className={`ml-2 text-[11px] tabular-nums ${audit.score > audit.prev_score ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {audit.score > audit.prev_score ? '+' : ''}{audit.score - audit.prev_score}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-neutral-500">{audit.last_run_at ? fmtAgo(audit.last_run_at) : 'never'}</span>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="admin-integrations-security-audit-chips">
              {Object.entries(audit.checks || {}).map(([k, v]) => {
                const st = v.status;
                const cls = st === 'pass'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : st === 'warn'
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : 'border-rose-300 bg-rose-50 text-rose-900';
                const title = v.detail || v.coverage_pct !== undefined
                  ? String(v.detail || `${v.coverage_pct}%`)
                  : '';
                return (
                  <span key={k}
                    className={`inline-flex items-center rounded-full border text-[11px] font-semibold px-2 py-0.5 ${cls}`}
                    data-testid={`admin-integrations-security-audit-chip-${k}`}
                    title={title}>
                    {k}: {st}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(data.checks || []).map((c) => (
          <Card key={c.key} className="border-neutral-200" data-testid={`integration-card-${c.key}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-primary-900 truncate">{c.name}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    {fmtAgo(c.checked_at)}{c.latency_ms != null ? ` - ${c.latency_ms}ms` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <UptimeSlaChip
                    integration={historyByKey[c.key]}
                    testId={`integration-sla-${c.key}`}
                  />
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_VARIANT[c.status] || STATUS_VARIANT.warning}`}
                        data-testid={`integration-status-${c.key}`}>
                    {STATUS_ICON[c.status] || <Circle className="h-3 w-3" />}
                    {c.status}
                  </span>
                </div>
              </div>
              <div className="text-xs text-neutral-700 break-words">{c.message}</div>
              {c.extra?.fix_now && (
                <Link
                  to={c.extra.fix_now}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                  data-testid={`integration-fixnow-${c.key}`}
                >
                  Fix now <ExternalLink className="h-3 w-3" />
                </Link>
              )}
              {c.extra && Object.keys(c.extra).length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpanded({ ...expanded, [c.key]: !expanded[c.key] })}
                  className="text-[10px] text-neutral-500 hover:text-neutral-800"
                  data-testid={`integration-expand-${c.key}`}
                >
                  {expanded[c.key] ? 'Hide details' : 'Show details'}
                </button>
              )}
              {expanded[c.key] && (
                <pre className="text-[10px] bg-neutral-50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(c.extra, null, 2)}
                </pre>
              )}
              <IntegrationHistorySparkline
                integration={historyByKey[c.key]}
                testId={`integration-sparkline-${c.key}`}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminIntegrationsPage;
